import React from 'react';

import {
  AccountBalance,
  AccountBox,
  AllInclusive,
  Assessment,
  AssignmentInd,
  AttachMoney,
  Business,
  CardGiftcard,
  CheckBoxOutlined,
  Computer,
  CreditCard,
  Devices,
  DirectionsRun,
  Dvr,
  DynamicFeed,
  Equalizer,
  ErrorOutline,
  ExitToApp,
  LocalGroceryStore,
  MergeType,
  People,
  PhoneIphone,
  PlusOne,
  Print,
  Receipt,
  Restaurant,
  RestaurantMenu,
  Settings,
  SupervisedUserCircle,
  SwapHoriz,
  TrendingUp,
  Work,
} from '@material-ui/icons';
import { DateTime } from 'luxon';
import { useHistory } from 'react-router';

import { PERMISSION_PATHS } from 'Components-redesign/PermissionChecker/consts';

import MenuItem from './MenuItem';
import { useSelector } from '../../Hooks';

import { Section } from '.';

const icons: Record<string, JSX.Element> = {
  dashboard: <TrendingUp htmlColor="#b0b3b6" />,
  devices: <Devices htmlColor="#b0b3b6" />,
  printers: <Print htmlColor="#b0b3b6" />,
  select: <SwapHoriz htmlColor="#b0b3b6" />,
  userManagement: <AccountBox htmlColor="#b0b3b6" />,
  staffManagement: <People htmlColor="#b0b3b6" />,
  engagement: <AllInclusive htmlColor="#b0b3b6" />,
  'gift-cards-beta': <CardGiftcard htmlColor="#f9ceff" />,
  licenses: <AssignmentInd htmlColor="#b0b3b6" />,
  'gift-cards': <CardGiftcard htmlColor="#b0b3b6" />,
  insights: <Equalizer htmlColor="#fafafa" />,
  storeManagement: <AccountBalance htmlColor="#b0b3b6" />,
  pos: <LocalGroceryStore htmlColor="#b0b3b6" />,
  pimlegacy: <Work htmlColor="#b0b3b6" />,
  pimpos: <Restaurant htmlColor="#b0b3b6" />,
  quicksight: <Equalizer htmlColor="#b0b3b6" />,
  adyen: <CreditCard htmlColor="#b0b3b6" />,
  express: <DirectionsRun htmlColor="#b0b3b6" />,
  monitoring: <Dvr htmlColor="#b0b3b6" />,
  reporting: <Receipt htmlColor="#b0b3b6" />,
  internalTools: <Settings htmlColor="#b0b3b6" />,
  logOut: <ExitToApp htmlColor="#b0b3b6" />,
  exit: <ExitToApp htmlColor="#b0b3b6" />,
  tsr: <RestaurantMenu htmlColor="#b0b3b6" />,
  emulators: <Computer htmlColor="#b0b3b6" />,
  companies: <Business htmlColor="#b0b3b6" />,
  app: <PhoneIphone htmlColor="#b0b3b6" />,
  integrations: <MergeType htmlColor="#b0b3b6" />,
  revenue: <AttachMoney htmlColor="#b0b3b6" />,
  accountingConfigs: <Assessment htmlColor="#b0b3b6" />,
  errors: <ErrorOutline htmlColor="#b0b3b6" />,
  ingredients: <Receipt htmlColor="#b0b3b6" />,
  onboarding: <CheckBoxOutlined htmlColor="#b0b3b6" />,
  'create-company': <PlusOne htmlColor="#b0b3b6" />,
  'super-admin-tools': <Settings htmlColor="#b0b3b6" />,
  organizations: <SupervisedUserCircle htmlColor="#b0b3b6" />,
  releases: <DynamicFeed htmlColor="#b0b3b6" />,
};

interface Props {
  section: Section;
  handleClick: () => void;
  menuItemOpen: string;
  handleClose: () => void;
  routes: string[];
  companyData: any;
}

const DrawerGroup: React.FC<Props> = ({
  section,
  handleClick,
  menuItemOpen,
  handleClose,
  routes,
  companyData,
}) => {
  const history = useHistory();
  const currentPath = history.location.pathname;

  const userPermissions = useSelector((state) => state.userPermissions);
  const integrations = useSelector((state) => state.integrations)?.companyIntegrations;
  const superAdminUserType = useSelector((state) => state.superAdminUserType);
  const isNorwayUser = superAdminUserType === 'BARCONTROL' || companyData?.country === 'NO';
  const isSpainUser =
    companyData?.country === 'ES' &&
    DateTime.fromISO(companyData?.createdAt) > DateTime.fromISO('2025-09-01'); // Old spaninh companies need to access VM as before

  // Check if user should see menu icon
  switch (section.id) {
    case 'pos':
      if ((companyData && !companyData.hasPOS) || isSpainUser) return null;
      break;

    case 'tsr':
      if ((companyData && !companyData.hasTables) || isSpainUser) return null;
      break;

    case 'express':
      if (companyData && !companyData.hasExpress) return null;
      break;

    case 'app':
      if ((companyData && !companyData.hasAppOrders) || isSpainUser) return null;
      break;

    case 'accountingConfigs':
      if (integrations?.length < 1) return null;
      break;

    case 'pimpos':
      if (isSpainUser) return null;
      break;

    case 'monitoring':
      if (isSpainUser) return null;
      break;

    case 'reporting':
      if (isSpainUser) return null;
      break;

    default:
      break;
  }

  const norwayHiddenRoutes = [
    'receiptTemplates',
    'messages',
    'stations',
    'reports',
    'fortnox-report',
    'z-reports',
    'integrations/personalkollen',
    'integrations/gstock',
    'integrations/list',
  ];

  const spainHiddenRoutes = [
    'receiptTemplates',
    'messages',
    'stations',
    'pim',
    'pos',
    'pimpos',
    'reports',
    'fortnox-report',
    'z-reports',
    'integrations/personalkollen',
    'integrations/gstock',
    'integrations/list',
    'signupcodes',
    'stampcards',
    'promocodes',
    'menus-select',
    'opening-hours',
    'beepers',
    'venues',
    'pos-item-report',
  ];

  // If beta route is not activated on company return;
  const betaSection = routes.find((route) => route.includes(section.id));
  if (section.id.includes('beta') && !betaSection) return null;

  // Filter out menu options the user should not see
  const filteredList = section.items.filter((item) => {
    const path = `/${item[0]}`; // Add "/" to match the keys in PERMISSION_PATHS

    // Retrieve required scopes for the current path from PERMISSION_PATHS
    const requiredScopes = PERMISSION_PATHS[path] || [];

    // Check if the user has any of the required scopes or if no permissions are required
    const hasPermissions =
      requiredScopes.length === 0 || // No permissions required
      userPermissions?.scopes.length === 0 || // Full access if user's scopes are empty
      requiredScopes.some((scope) => userPermissions?.scopes.includes(scope));

    // console.log('item', item);

    // Additional filtering rules for hiding specific routes
    const foundBetaRoute = routes.find((route) => route === item[0]);
    if (companyData && !companyData.hasInventory && item[0] === 'inventory') {
      return false;
    } else if (item[0].includes('beta') && !foundBetaRoute) {
      return false;
    } else if (isNorwayUser && norwayHiddenRoutes.includes(item[0])) {
      return false;
    } else if (isSpainUser && spainHiddenRoutes.includes(item[0])) {
      return false;
    } else {
      // Only return true if the user has permissions for the path
      return hasPermissions;
    }
  });

  let isActive = currentPath.split('/')[1].includes(section.id);
  if (
    (currentPath.split('/')[1] == 'pos' || currentPath.split('/')[1] == 'pim') &&
    section.id == 'pimpos'
  ) {
    isActive = true;
  }

  return (
    <MenuItem
      id={section.id}
      icon={icons[section.id]}
      name={section.title}
      active={isActive}
      listItems={filteredList}
      onClick={handleClick}
      menuItemOpen={menuItemOpen}
      handleClose={handleClose}
    />
  );
};

export default DrawerGroup;
